/**
 * The boss tier: ONE concept, one type, one table.
 *
 * ## Why this module exists at all
 *
 * The six tiers were born inside the loot module, because the first thing that
 * needed them was the rare special draw. They are not a loot concept: a tier
 * decides how big a boss is drawn and how much Gold it guarantees, and the drop
 * odds are only the third thing that reads it. Leaving the type there meant the
 * next feature that needed a tier would either import the loot module - dragging
 * HMAC and rarity tables behind it - or declare a second `BossTier`, which is
 * how a codebase ends up with two six-value enums that drift.
 *
 * So the type and the per-tier facts live here, in a module that imports
 * nothing. The loot table imports this; this imports nothing back, so there is
 * no cycle to break later.
 *
 * ## What a tier is and is not
 *
 * A tier is SIZE and REWARD. Within a map, every tier has exactly the same
 * health, damage, speed, escape timer and XP - a tier 6 is a bigger boss worth
 * more Gold, never a harder fight. That separation is deliberate: meeting the
 * largest boss should feel lucky rather than punishing.
 *
 * ## Integers only
 *
 * Sizes in basis points, appearance odds in whole parts of ten thousand, Gold as
 * a whole multiplier applied to a `bigint`. A percentage held as a double is a
 * percentage that does not sum to one hundred, and the tests assert that these
 * do - by equality.
 */

export const BOSS_TIERS = [1, 2, 3, 4, 5, 6] as const;
export type BossTier = (typeof BOSS_TIERS)[number];

/**
 * The tier the shipped boss is treated as until encounters are persisted.
 *
 * The simulation already rolls a boss `scale`, and mapping it onto these six is
 * tempting and wrong: that scale comes from a SIMULATION stream whose seed is
 * public, and a death or an F5 mints a new seed - so a player could keep
 * restarting until the tier they will be rolled against looks good. The drop
 * roll itself is a keyed HMAC they cannot predict, so this would not force a
 * drop; it would let them shop for the odds it is drawn against, which is the
 * same defect one step removed.
 *
 * Deliberate under-crediting until boss size becomes authoritative, persisted
 * state. A boss quietly worth more than it should be is far harder to notice
 * than one worth less.
 */
export const DEFAULT_BOSS_TIER: BossTier = 1;

/** Basis points: 10 000 is the map's own mob, unscaled. */
export const BOSS_TIER_DENOMINATOR_BPS = 10_000;

/** Whole parts of ten thousand. The six sum to exactly this. */
export const BOSS_APPEARANCE_DENOMINATOR = 10_000;

export interface BossTierDefinition {
  readonly tier: BossTier;
  /** How much larger than the map's mob, in basis points. */
  readonly sizeMultiplierBps: number;
  /** How often this tier comes up, in parts of `BOSS_APPEARANCE_DENOMINATOR`. */
  readonly appearanceParts: number;
  /** Multiplies the map's base boss Gold. A whole number, never a fraction. */
  readonly goldMultiplier: number;
}

const DEFINITIONS: Readonly<Record<BossTier, BossTierDefinition>> = Object.freeze({
  1: Object.freeze({
    tier: 1,
    sizeMultiplierBps: 13_000,
    appearanceParts: 2_500,
    goldMultiplier: 1,
  }),
  2: Object.freeze({
    tier: 2,
    sizeMultiplierBps: 15_500,
    appearanceParts: 2_000,
    goldMultiplier: 2,
  }),
  3: Object.freeze({
    tier: 3,
    sizeMultiplierBps: 18_500,
    appearanceParts: 1_700,
    goldMultiplier: 3,
  }),
  4: Object.freeze({
    tier: 4,
    sizeMultiplierBps: 22_000,
    appearanceParts: 1_500,
    goldMultiplier: 4,
  }),
  5: Object.freeze({
    tier: 5,
    sizeMultiplierBps: 26_000,
    appearanceParts: 1_300,
    goldMultiplier: 5,
  }),
  /** Three times the map's mob - 200% larger - on exactly one encounter in ten. */
  6: Object.freeze({
    tier: 6,
    sizeMultiplierBps: 30_000,
    appearanceParts: 1_000,
    goldMultiplier: 6,
  }),
});

export function bossTierDefinition(tier: BossTier): BossTierDefinition {
  const definition = DEFINITIONS[tier];
  if (!definition) {
    throw new RangeError(`not a boss tier: ${String(tier)}`);
  }
  return definition;
}

export const BOSS_TIER_DEFINITIONS: readonly BossTierDefinition[] = Object.freeze(
  BOSS_TIERS.map((tier) => bossTierDefinition(tier)),
);

/**
 * Gold a confirmed boss kill guarantees: the map's base, times the tier.
 *
 * `bigint` throughout because this enters the ledger, where a currency has
 * never been a JavaScript number. A boss that ESCAPES pays nothing - that is
 * the only lever the player has over the outcome, and it is the honest one.
 */
export function bossGoldForTier(baseGold: bigint, tier: BossTier): bigint {
  return baseGold * BigInt(bossTierDefinition(tier).goldMultiplier);
}

/**
 * The boss's visual scale in basis points: the map's mob scale, times the tier.
 *
 * Both terms are basis points, so the product is divided back down once. Kept in
 * `bigint` because `mobScaleBpsForMap` is - map 50 is 1,1^49 and the whole point
 * of that function is that it never touches a float.
 */
export function bossVisualScaleBps(mobScaleBps: bigint, tier: BossTier): bigint {
  return (
    (mobScaleBps * BigInt(bossTierDefinition(tier).sizeMultiplierBps)) /
    BigInt(BOSS_TIER_DENOMINATOR_BPS)
  );
}

/**
 * The width a boss is drawn at, bounded by the CANONICAL arena.
 *
 * ## The arena is a constant, never the browser
 *
 * Two players on different screens meeting the same encounter must see the same
 * boss, and the same account must see the same one on a phone and a monitor. So
 * the width is computed against the arena the simulation is defined in, and the
 * canvas only scales that arena to fit. A boss sized from the viewport would be
 * a different boss on every device, and its hitbox would stop agreeing with its
 * sprite.
 *
 * ## The cap bounds the drawing, not the tier
 *
 * A tier 6 on a late map genuinely wants to be wider than the arena. It may
 * fill the arena completely and never exceed it: cut off at the edge it would
 * be unhittable at its extremes and its health bar would run off screen. The
 * tier is unchanged - only the drawn width is bounded.
 */
export function cappedBossWidth(
  normalMobWidth: number,
  mobScaleBps: bigint,
  tier: BossTier,
  canonicalArenaWidth: number,
): number {
  const scaleBps = bossVisualScaleBps(mobScaleBps, tier);
  // The single conversion to `number`, at the rendering edge and nowhere else.
  const width = (normalMobWidth * Number(scaleBps)) / BOSS_TIER_DENOMINATOR_BPS;
  return Math.min(width, canonicalArenaWidth);
}

/** True when the arena, rather than the tier, decided the width. */
export function bossWidthIsCapped(
  normalMobWidth: number,
  mobScaleBps: bigint,
  tier: BossTier,
  canonicalArenaWidth: number,
): boolean {
  const uncapped =
    (normalMobWidth * Number(bossVisualScaleBps(mobScaleBps, tier))) / BOSS_TIER_DENOMINATOR_BPS;
  return uncapped > canonicalArenaWidth;
}

/* -------------------------------------------------------------------------- */
/*  Encounter timing                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The window a boss arrives in, in milliseconds of ACTIVE hunting.
 *
 * Only the bounds live here. Drawing a delay inside them, freezing it and
 * persisting it belongs with the encounter itself, where the entropy can be
 * Web Crypto - a public simulation seed must never decide it.
 */
export const BOSS_DELAY_MIN_MS = 3 * 60 * 1000;
export const BOSS_DELAY_MAX_MS = 16 * 60 * 1000;
